import { useState } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { useMutation } from '@apollo/client';
import { Link } from 'react-router-dom';
import { TERMS_CONTENT, PRIVACY_CONTENT, CURRENT_TERMS_VERSION, CURRENT_PRIVACY_VERSION } from '../../config/legal';
import { ACCEPT_TERMS } from '../../graphql/feedback';
import { getCurrentIsoWithTimezoneOffset } from '../../utils/legalAcceptance';
import { renderLegalMarkdown } from '../../utils/legalMarkdown';
import { useError } from '../../hooks/useError.js';
import { useToast } from '../../hooks/useToast.js';

const TermsAcceptanceGate = ({ isOpen, onClose, onAccepted, isDarkTheme }) => {
  const [tab, setTab] = useState('terms');
  const [accepted, setAccepted] = useState(false);
  const [error, setError] = useState(null);
  const { showError } = useError();
  const toast = useToast();
  const [acceptTerms, { loading }] = useMutation(ACCEPT_TERMS);

  if (!isOpen) return null;

  const html = renderLegalMarkdown(tab === 'terms' ? TERMS_CONTENT : PRIVACY_CONTENT);

  const handleAccept = async () => {
    if (!accepted) {
      setError('Marque a caixa para confirmar o aceite.');
      return;
    }
    try {
      await acceptTerms({ variables: { acceptedAtIso: getCurrentIsoWithTimezoneOffset() } });
      toast.success('Termos aceitos com sucesso.');
      onAccepted?.();
    } catch (err) {
      setError(showError(err, { fallback: 'Falha ao registrar aceite.' }));
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div
        className={clsx(
          'w-full max-w-2xl rounded-2xl shadow-xl flex flex-col max-h-[90vh]',
          isDarkTheme ? 'bg-greenbook-dark-bg text-greenbook-dark-text' : 'bg-white text-gray-900'
        )}
      >
        <div className="p-6 pb-3 border-b border-gray-200 dark:border-gray-800">
          <h2 className="text-xl font-semibold">Termos e Privacidade</h2>
          <p className="text-xs mt-1 text-gray-600 dark:text-gray-300">
            Versão Termos: {CURRENT_TERMS_VERSION} · Versão Privacidade: {CURRENT_PRIVACY_VERSION}
          </p>
          <div className="flex gap-2 mt-3 text-sm">
            <button
              type="button"
              onClick={() => setTab('terms')}
              className={clsx(
                'px-3 py-1 rounded-lg',
                tab === 'terms' ? 'bg-greenbook-light-primary text-white' : 'hover:bg-gray-100 dark:hover:bg-gray-800'
              )}
            >
              Termos de Uso
            </button>
            <button
              type="button"
              onClick={() => setTab('privacy')}
              className={clsx(
                'px-3 py-1 rounded-lg',
                tab === 'privacy' ? 'bg-greenbook-light-primary text-white' : 'hover:bg-gray-100 dark:hover:bg-gray-800'
              )}
            >
              Política de Privacidade
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          <div
            className="legal-content"
            dangerouslySetInnerHTML={{ __html: html }}
          />
        </div>

        <div className="p-6 pt-3 border-t border-gray-200 dark:border-gray-800 space-y-3">
          <label className="flex gap-2 items-start text-sm">
            <input
              type="checkbox"
              className="mt-0.5"
              checked={accepted}
              onChange={(e) => setAccepted(e.target.checked)}
            />
            <span>
              Li e aceito os{' '}
              <Link to="/termos" className="underline" target="_blank" rel="noreferrer">Termos de Uso</Link>{' '}
              e a{' '}
              <Link to="/politica-de-privacidade" className="underline" target="_blank" rel="noreferrer">Política de Privacidade</Link>.
            </span>
          </label>
          {error && <p className="text-sm text-red-600 dark:text-red-300">{error}</p>}
          <div className="flex justify-end gap-2">
            <button
              type="button"
              className={clsx('px-4 py-2 rounded-lg text-sm', isDarkTheme ? 'hover:bg-gray-800' : 'hover:bg-gray-100')}
              onClick={onClose}
              disabled={loading}
            >
              Cancelar
            </button>
            <button
              type="button"
              className="px-4 py-2 rounded-lg text-sm font-semibold bg-greenbook-light-primary text-white hover:bg-greenbook-light-green hover:text-green-900 disabled:opacity-60"
              onClick={handleAccept}
              disabled={loading}
            >
              {loading ? 'Salvando…' : 'Aceitar e continuar'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

TermsAcceptanceGate.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onAccepted: PropTypes.func,
  isDarkTheme: PropTypes.bool,
};

TermsAcceptanceGate.defaultProps = {
  onAccepted: undefined,
  isDarkTheme: false,
};

export default TermsAcceptanceGate;
